import { FC, useState, useRef, useEffect } from 'react';

import { useNovaPoshtaSearch } from '../../hooks/useNovaPoshtaSearch';
import { Routes } from '../../models/routes';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

interface City {
    ref: string;
    name: string;
    area: string;
}

interface Props {
    value: string; // displayed city name
    onSelect: (city: { ref: string; name: string }) => void;
    onFallbackInput: (value: string) => void;
    onSelectionClear?: (value: string) => void;
    error?: string;
    initialFallback?: boolean;
    allowManualEntry?: boolean;
    id?: string;
    label?: string;
    required?: boolean;
}

export const NovaPoshtaCitySelect: FC<Props> = ({
    value,
    onSelect,
    onFallbackInput,
    onSelectionClear,
    error,
    initialFallback = false,
    allowManualEntry = true,
    id = 'np_city',
    label = 'Місто',
    required = true,
}) => {
    const [query, setQuery] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const [manualFallback] = useState(initialFallback);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { results, isFallback, search, clear } = useNovaPoshtaSearch<City>({
        url: Routes.ApiNovaPoshtaCities,
        enabled: allowManualEntry ? !manualFallback : true,
    });

    const useFallback = allowManualEntry && (manualFallback || isFallback);

    useEffect(() => {
        if (!isOpen) {
            setQuery(value);
        }
    }, [isOpen, value]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (val: string) => {
        setQuery(val);
        if (useFallback) {
            onFallbackInput(val);
        } else {
            if (val !== value) {
                onSelectionClear?.(val);
            }
            search(val);
            setIsOpen(true);
        }
    };

    const handleSelect = (city: City) => {
        setQuery(city.name);
        setIsOpen(false);
        clear();
        onSelect({ ref: city.ref, name: city.name });
    };

    const handleBlur = () => {
        window.setTimeout(() => setIsOpen(false), 0);
    };

    return (
        <div ref={wrapperRef} className="relative space-y-1">
            <Label htmlFor={id}>
                {label}
                {required ? ' *' : ''}
            </Label>
            <Input
                id={id}
                value={query}
                onChange={(e) => handleInputChange(e.target.value)}
                onBlur={handleBlur}
                placeholder="Почніть вводити назву міста..."
                className={error ? 'border-red-500' : ''}
                autoComplete="off"
                required={required}
            />
            {/* {isLoading && (
                <p className="text-xs text-muted-foreground">Пошук...</p>
            )} */}
            {error && <p className="text-xs text-red-500">{error}</p>}
            {!allowManualEntry && isFallback && (
                <p className="text-xs text-red-500">
                    Не вдалося завантажити список міст. Спробуйте ще раз.
                </p>
            )}

            {isOpen && results.length > 0 && (
                <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-white shadow-lg dark:border-white/10 dark:bg-slate-900 dark:shadow-2xl">
                    {results.map((city) => (
                        <li
                            key={city.ref}
                            className="cursor-pointer px-3 py-2 text-sm hover:bg-muted/50 dark:text-slate-100 dark:hover:bg-slate-800"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleSelect(city)}
                        >
                            <span className="font-medium">{city.name}</span>
                            {city.area && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                    {city.area}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
